/* ============================================================
   票面布局：屏幕（HTML/CSS）与导出（Canvas）的**唯一来源**
   ------------------------------------------------------------
   为什么要有这个文件：票面既要印在屏幕上的票版图层里，又要画进导出的
   1080×1440 PNG。两边各自写一套坐标必然会漂——之前屏幕和导出就是两张
   完全不同的票面。这里把几何与字号比例集中定义，屏幕侧经 CSS 变量消费，
   导出侧直接换算成画布像素。

   坐标系：素材归一化坐标（0—1，相对整幅票版素材）。
   坐标全部是实测值，不是目测：
     - 纸面边界：scripts/_probe-paper.mjs（高亮度 + 低饱和判据，
       用来排除落日、红绸与暗木）
     - 朱印：2% 细网格放大图 qa/_diag/seal-*.png 读数
   ============================================================ */

const mobileArt = new URL("../assets/v4/mobile/ticket.webp", import.meta.url).href;
const desktopArt = new URL("../assets/v4/desktop/ticket.webp", import.meta.url).href;

export type TicketArtwork = {
  /** 素材地址 */
  src: string;
  /** 素材宽高比（宽 / 高），用于复刻 background-size: cover 的裁剪 */
  ratio: number;
  width: number;
  height: number;
};

export type TicketGeometry = {
  /** 「标题 / 我的答案 / 答案 / 编号」文字块（相对素材，旋转前的位置） */
  fields: { left: number; top: number; width: number };
  /** 文字块旋转角度（度）。桌面票版整张纸倾斜，必须跟着转 */
  fieldsRotate: number;
  /** 文字块对齐方式。移动端居中（与朱印同轴），桌面保持左对齐 */
  align: "left" | "center";
  /** 空白朱印内部，用于放「已启航」 */
  seal: { left: number; top: number; width: number; height: number };
  sealRotate: number;
  /** 字号 = 素材宽度 × 该比例（屏幕侧再套 clamp 兜住可读性下限） */
  font: { head: number; label: number; answer: number; no: number; seal: number };
  /** 行间距 = 素材宽度 × 该比例 */
  gap: { label: number; answer: number; no: number };
  /** 答案最多显示几行（导出与屏幕都遵守） */
  answerLines: number;
  /** 答案行高倍数（与 CSS line-height 保持一致） */
  answerLineHeight: number;
};

export const TICKET_ARTWORK: Record<"mobile" | "desktop", TicketArtwork> = {
  mobile: { src: mobileArt, ratio: 941 / 1671, width: 941, height: 1671 },
  desktop: { src: desktopArt, ratio: 1672 / 941, width: 1672, height: 941 },
};

export const TICKET_GEOMETRY: Record<"mobile" | "desktop", TicketGeometry> = {
  /* 竖版票版：纸面内部约 x 24—70%（中心 47%）、y 30% 起；朱印 x 38.8—63.4% / y 52.3—64.8%。
     卡片在画面里**是斜的**：上边缘从 x12% 的 y23.2% 降到 x65% 的 y25.5%
     （细网格读数，见 qa/_diag/edge-mobile.png），即顺时针约 4.4°。
     文字必须跟着斜——否则"正着的字压在斜着的票上"，一眼就假。
     旋转会让文字块的纵向占用变大（宽 × sin4.4° ≈ 32px），所以起点提到 0.29。 */
  mobile: {
    fields: { left: 0.25, top: 0.29, width: 0.44 },
    fieldsRotate: 4.4,
    align: "center",
    seal: { left: 0.39, top: 0.523, width: 0.244, height: 0.125 },
    sealRotate: 4.4,
    font: { head: 0.046, label: 0.023, answer: 0.0355, no: 0.019, seal: 0.05 },
    gap: { label: 0.026, answer: 0.018, no: 0.024 },
    answerLines: 4,
    answerLineHeight: 1.55,
  },
  /* 横版票版：整张纸也倾斜（实测上边缘 7.3°、下边缘 8.6°，取 8°）。
     保持左对齐——桌面票面又宽又矮（纸面内部纵向约 y 22%—80%，扣掉旋转摆动后可用约 50%
     画布高），所以这套比例是"3 行答案刚好装下"的解：再大就会把答案截断打省略号（丢内容），
     再小则字在大票面上显得"不在票上"。 */
  desktop: {
    fields: { left: 0.30, top: 0.30, width: 0.36 },
    fieldsRotate: 8,
    align: "left",
    seal: { left: 0.70, top: 0.432, width: 0.114, height: 0.18 },
    sealRotate: 8,
    font: { head: 0.038, label: 0.019, answer: 0.030, no: 0.016, seal: 0.031 },
    gap: { label: 0.016, answer: 0.010, no: 0.012 },
    answerLines: 3,
    answerLineHeight: 1.55,
  },
};

/**
 * 导出参数按视口档位取：桌面用户应当拿到**横版票版**（也就是他在屏幕上看到的那张），
 * 而不是手机版的竖构图。此前的实现把导出写死成竖版素材，桌面端下载到的是手机版画面。
 *
 * 画布尺寸各自贴合对应素材的宽高比：
 *   竖版 941×1671 → 1080×1440（略裁上下，票面完整）
 *   横版 1672×941 → 1920×1080（与素材同比例，完全不裁）
 */
export const TICKET_EXPORT_CANVAS: Record<"mobile" | "desktop", { width: number; height: number }> = {
  mobile: { width: 1080, height: 1440 },
  desktop: { width: 1920, height: 1080 },
};

export function ticketExportFor(profile: "mobile" | "tablet" | "desktop"): {
  art: TicketArtwork;
  geometry: TicketGeometry;
  canvas: { width: number; height: number };
} {
  const key = profile === "mobile" ? "mobile" : "desktop";
  return { art: TICKET_ARTWORK[key], geometry: TICKET_GEOMETRY[key], canvas: TICKET_EXPORT_CANVAS[key] };
}

/**
 * 票幕该用哪一版票版（也决定导出拿竖版还是横版）：只看窗口朝向。
 *
 * 屏幕上的票面是"印"在背景素材上的叠加层，它必须待在 `.v2-bg-current` 里才能与素材
 * 共享那两层动画（13s/3.4s 缓推 + 50s 漂移）——代价是它也继承了 cover 裁切。
 * 素材与窗口朝向不一致时，叠加层会跟着一起出画，两个方向都会坏：
 *   竖屏窗口 + 横版素材（1672×941）：768×1024 实测标题左缘切 47px、答案切 85px、
 *   朱印整枚 0% 可见（1024×1366 更差）；
 *   横屏窗口 + 竖版素材（941×1671）：568×320 实测标题被切到视口上方。
 * 所以宽高比 < 1 用竖版、≥ 1 用横版。**不看 profile**：768×1024 属于 tablet 档却要竖版，
 * 568×320 属于 mobile 档却要横版，profile 与"该用哪张纸"本来就是两件事。
 *
 * 切了素材就必须同时切几何、CSS 变量与导出尺寸，否则字会印到票外——
 * 调用方一律用本函数的返回值，不要再各判一次 profile。
 *
 * 已知残差：宽高比 1.0–1.45 之间仍用横版素材，此时素材按高度铺满、左右略溢出，
 * 朱印右缘会被切掉一角（1280×1024 切 53px ≈ 21%，1024×768 切 19px）。
 * 文字块不受影响；再往竖里切会反过来把标题顶到视口上沿，所以取 1 作为分界。
 */
export function ticketArtKeyFor(aspect: number): "mobile" | "desktop" {
  return aspect < 1 ? "mobile" : "desktop";
}
